/**
 * 국내 결정례 합의 — 사전 점검 카드가 받는 precedent_hints(세번별 건수, 폐기 결정례 제외)에서 트리 범위 안의
 * 결정례가 한 세번으로 모이는지 본다. 검색 표본 전체를 분모로 쓰며 결과는 후보 표시용이다.
 * 호·소호나 상품 사실을 자동으로 입력하지 않는다.
 */
export const CONSENSUS_MIN_COUNT = 3;
export const CONSENSUS_MIN_SHARE = 0.8;

const digits = (value) => String(value || "").replace(/\D/g, "");

/**
 * @param hints precedent_hints 페이로드({ by_hsk: [{ hsk, count, latest_date, label }], domestic_total })
 * @param scopePrefixes 트리 범위 접두(호 4자리 또는 류 2자리). 비어 있으면 전체를 본다.
 * @returns null | { hsk, count, total, share, latest_date, label }
 */
export function precedentConsensus(hints, scopePrefixes = []) {
  const rows = Array.isArray(hints?.by_hsk) ? hints.by_hsk : [];
  const prefixes = (Array.isArray(scopePrefixes) ? scopePrefixes : []).map(digits).filter(Boolean);
  const valid = rows.filter((row) => row && digits(row.hsk).length === 10
    && Number.isFinite(Number(row.count)) && Number(row.count) > 0);
  const inScope = valid
    .filter((row) => prefixes.length === 0 || prefixes.some((p) => digits(row.hsk).startsWith(p)));
  if (inScope.length === 0) return null;
  const representedTotal = valid.reduce((sum, row) => sum + Number(row.count), 0);
  const reportedTotal = Number(hints?.eligible_total ?? (Number(hints?.domestic_total || 0) - Number(hints?.superseded || 0)));
  const total = Math.max(representedTotal, Number.isFinite(reportedTotal) ? reportedTotal : 0);
  const top = inScope.reduce((best, row) => (Number(row.count) > Number(best.count) ? row : best), inScope[0]);
  const count = Number(top.count || 0);
  const share = total > 0 ? count / total : 0;
  if (count < CONSENSUS_MIN_COUNT || share < CONSENSUS_MIN_SHARE) return null;
  return {
    hsk: digits(top.hsk),
    count,
    total,
    share,
    outside_count: valid.filter((row) => !inScope.includes(row)).reduce((sum, row) => sum + Number(row.count), 0),
    latest_date: top.latest_date || "",
    label: top.label || "",
  };
}

/**
 * 제목 일치 합의(2026-09-15) — 검색어와 **제목이 같은** 국내 결정례가 1건 이상이고 그것들이 한 세번으로만 모이면 그 세번.
 * 일반 합의(3건·80%)가 잡음 사례("~라면" 조사, 샴푸 펌프) 때문에 거의 켜지지 않던 것을 보완한다. 확정이 아니다.
 * @returns null | { hsk, count, total, share: 1, outside_count: 0, latest_date, label, title: true, corrected }
 */
export function precedentTitleConsensus(hints) {
  const total = Number(hints?.title_exact_total || 0);
  const hsks = Number(hints?.title_exact_hsks || 0);
  const hsk = digits(hints?.title_exact_hsk);
  if (total < 1 || hsks !== 1 || hsk.length !== 10) return null;
  const row = (Array.isArray(hints?.by_hsk) ? hints.by_hsk : []).find((r) => digits(r?.hsk) === hsk) || {};
  return { hsk, count: total, total, share: 1, outside_count: 0, latest_date: row.latest_date || "", label: row.label || "", title: true, corrected: String(hints?.corrected_query || "") };
}

/** 합의 세번과 트리 결과의 관계. 카드 배너 상태를 결정한다. */
export function consensusRelation(consensus, tree) {
  if (!consensus) return "none";
  const supported = digits(tree?.supported_hsk);
  if (supported) return supported === consensus.hsk ? "matched" : "differs";
  const hs6 = digits(tree?.supported_hs6);
  if (hs6 && !consensus.hsk.startsWith(hs6)) return "differs";
  const hs4 = digits(tree?.supported_hs4);
  if (hs4 && !consensus.hsk.startsWith(hs4)) return "differs";
  if (String(tree?.action || "") === "out_of_scope") return "differs";
  return "pending";
}
